import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Accordion, AccordionSummary, AccordionDetails,
    Button, Grid, Snackbar, Alert, Dialog, Pagination, Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BuildingDetailsWallsAddWall from './BuildingDetailsWallsAddWall';
import BuildingDetailsWallsAddDoor from './BuildingDetailsWallsAddDoor';
import BuildingDetailsWallsAddWindow from "./BuildingDetailsWallsAddWindow";

const BuildingDetailsWalls = () => {
    const { id } = useParams();
    const [walls, setWalls] = useState([]);
    const [page, setPage] = useState(1);
    const wallsPerPage = 5;
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [modalOpen, setModalOpen] = useState(false);
    const [doorModalOpen, setDoorModalOpen] = useState(false);
    const [selectedWall, setSelectedWall] = useState(null);
    const [selectedWallForDoor, setSelectedWallForDoor] = useState(null);
    const [selectedDoor, setSelectedDoor] = useState(null);
    const [windowModalOpen, setWindowModalOpen] = useState(false);
    const [selectedWallForWindow, setSelectedWallForWindow] = useState(null);
    const [selectedWindow, setSelectedWindow] = useState(null);

    const fetchWalls = () => {
        axios.get(`/buildings/${id}/walls/`)
            .then(async res => {
                const wallData = await Promise.all(res.data.map(async wall => {
                    try {
                        const doorsRes = await axios.get(`/buildings/${id}/walls/${wall.id}/doors/`);
                        const windowsRes = await axios.get(`/buildings/${id}/walls/${wall.id}/windows/`);
                        return { ...wall, doors: doorsRes.data, windows: windowsRes.data };
                    } catch {
                        return { ...wall, doors: [], windows: [] };
                    }
                }));
                setWalls(wallData);
            })
            .catch(() => {
                setSnackbar({ open: true, message: 'Failed to fetch walls.', severity: 'error' });
            });
    };

    useEffect(() => {
        fetchWalls();
    }, [id]);

    const handleDelete = (wallId) => {
        axios.delete(`/buildings/${id}/walls/${wallId}/`)
            .then(() => {
                setSnackbar({ open: true, message: 'Wall deleted successfully.', severity: 'success' });
                fetchWalls();
            })
            .catch(() => {
                setSnackbar({ open: true, message: 'Failed to delete wall.', severity: 'error' });
            });
    };

    const deleteDoor = (wallId, doorId) => {
        axios.delete(`/buildings/${id}/walls/${wallId}/doors/${doorId}/`)
            .then(() => {
                setSnackbar({ open: true, message: 'Door deleted successfully.', severity: 'success' });
                fetchWalls();
            })
            .catch(() => {
                setSnackbar({ open: true, message: 'Failed to delete door.', severity: 'error' });
            });
    };

    const deleteWindow = (wallId, windowId) => {
        axios.delete(`/buildings/${id}/walls/${wallId}/windows/${windowId}/`)
            .then(() => {
                setSnackbar({ open: true, message: 'Window deleted successfully.', severity: 'success' });
                fetchWalls();
            })
            .catch(() => {
                setSnackbar({ open: true, message: 'Failed to delete window.', severity: 'error' });
            });
    };

    const indexOfLast = page * wallsPerPage;
    const indexOfFirst = indexOfLast - wallsPerPage;
    const currentWalls = walls.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(walls.length / wallsPerPage);

    return (
        <Box sx={{ my: 5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Walls</Typography>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
                    Add Wall
                </Button>
            </Box>

            {currentWalls.map((wall) => (
                <Accordion
                    sx={{
                        mb: 2, borderRadius: 2, boxShadow: 'none',
                        border: '1px solid #e0e0e0', '&::before': { display: 'none' }
                    }}
                    key={wall.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>{wall.name}</Typography>
                        <Button variant="outlined" color="secondary" size="small" sx={{ mr: 1 }} onClick={(e) => {
                            e.stopPropagation();
                            axios.get(`/buildings/${id}/walls/${wall.id}/`).then(res => {
                                setSelectedWall(res.data);
                                setModalOpen(true);
                            }).catch(() => {
                                setSnackbar({ open: true, message: 'Failed to load wall for editing.', severity: 'error' });
                            });
                        }}>Edit</Button>
                        <Button variant="contained" color="error" size="small" sx={{ mr: 2 }} onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(wall.id);
                        }}>Delete</Button>
                    </AccordionSummary>

                    <AccordionDetails sx={{ backgroundColor: '#f5f9f6' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">Area</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{wall.area}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">Orientation</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{wall.orientation}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">Shading</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{wall.shading}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">U-Value (Insulated)</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{wall.u_value}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography variant="subtitle2">U-Value (Uninsulated)</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{wall.u_value_uninsulated}</Typography>
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mt: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Doors</Typography>
                                <Button variant="outlined" color="info" size="small" sx={{ textTransform: 'uppercase', fontWeight: 600 }} onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedWallForDoor(wall);
                                    setSelectedDoor(null);
                                    setDoorModalOpen(true);
                                }}>
                                    Add Door
                                </Button>
                            </Box>

                            {wall.doors.map(door => (
                                <Box key={door.id} sx={{ p: 2, mb: 2, borderRadius: 2, backgroundColor: '#fff', border: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{door.name}</Typography>
                                        <Typography variant="body2">Area: {door.area} | U-Value: {door.u_value}</Typography>
                                    </Box>
                                    <Box>
                                        <Button variant="outlined" onClick={() => {
                                            setSelectedWallForDoor(wall);
                                            setSelectedDoor(door);
                                            setDoorModalOpen(true);
                                        }} size="small" sx={{ mr: 1 }}>Edit Door</Button>
                                        <Button variant="contained" color="error" size="small" onClick={() => deleteDoor(wall.id, door.id)}>Delete</Button>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mt: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Windows</Typography>
                                <Button variant="outlined" color="info" size="small" sx={{ textTransform: 'uppercase', fontWeight: 600 }} onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedWallForWindow(wall);
                                    setSelectedWindow(null);
                                    setWindowModalOpen(true);
                                }}>
                                    Add Window
                                </Button>
                            </Box>

                            {wall.windows.map(window => (
                                <Box key={window.id} sx={{ p: 2, mb: 2, borderRadius: 2, backgroundColor: '#fff', border: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{window.name}</Typography>
                                        <Typography variant="body2">Area: {window.area} | U-Value: {window.u_value}</Typography>
                                    </Box>
                                    <Box>
                                        <Button variant="outlined" onClick={() => {
                                            setSelectedWallForWindow(wall);
                                            setSelectedWindow(window);
                                            setWindowModalOpen(true);
                                        }} size="small" sx={{ mr: 1 }}>Edit Window</Button>
                                        <Button variant="contained" color="error" size="small" onClick={() => deleteWindow(wall.id, window.id)}>Delete</Button>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}

            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" shape="rounded" />
                </Box>
            )}

            <Dialog open={modalOpen} onClose={() => {
                setModalOpen(false);
                setSelectedWall(null);
            }}>
                <BuildingDetailsWallsAddWall
                    buildingId={id}
                    wall={selectedWall}
                    onSuccess={() => {
                        fetchWalls();
                        setSnackbar({
                            open: true,
                            message: selectedWall ? 'Wall updated successfully!' : 'Wall added successfully!',
                            severity: 'success'
                        });
                        setSelectedWall(null);
                    }}
                    onError={() => {
                        setSnackbar({ open: true, message: 'Failed to save wall.', severity: 'error' });
                    }}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedWall(null);
                    }}
                />
            </Dialog>

            <Dialog open={doorModalOpen} onClose={() => {
                setDoorModalOpen(false);
                setSelectedDoor(null);
            }}>
                <BuildingDetailsWallsAddDoor
                    buildingId={id}
                    wall={selectedWallForDoor}
                    door={selectedDoor} // undefined for Add, filled for Edit
                    onSuccess={() => {
                        fetchWalls();
                        setSnackbar({
                            open: true,
                            message: selectedDoor ? 'Door updated successfully!' : 'Door added successfully!',
                            severity: 'success'
                        });
                        setDoorModalOpen(false);
                        setSelectedDoor(null);
                    }}
                    onError={() => {
                        setSnackbar({ open: true, message: 'Failed to save door.', severity: 'error' });
                    }}
                    onClose={() => {
                        setDoorModalOpen(false);
                        setSelectedDoor(null);
                    }}
                />
            </Dialog>

            <Dialog open={windowModalOpen} onClose={() => {
                setWindowModalOpen(false);
                setSelectedWindow(null);
            }}>
                <BuildingDetailsWallsAddWindow
                    buildingId={id}
                    wall={selectedWallForWindow}
                    window={selectedWindow} // undefined for Add, filled for Edit
                    onSuccess={() => {
                        fetchWalls();
                        setSnackbar({
                            open: true,
                            message: selectedWindow ? 'Window updated successfully!' : 'Window added successfully!',
                            severity: 'success'
                        });
                        setWindowModalOpen(false);
                        setSelectedWindow(null);
                    }}
                    onError={() => {
                        setSnackbar({ open: true, message: 'Failed to save window.', severity: 'error' });
                    }}
                    onClose={() => {
                        setWindowModalOpen(false);
                        setSelectedWindow(null);
                    }}
                />
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>


        </Box>
    );
};

export default BuildingDetailsWalls;
